<template>
	<section
		class="office-hours-section new-settings__section"
		aria-labelledby="office-hours-section-title"
	>
		<header class="new-settings__section-header">
			<div class="new-settings__section-header-row">
				<div class="new-settings__section-header-text">
					<h1
						id="office-hours-section-title"
						class="new-settings__section-title"
					>
						Office Hours
					</h1>
					<p class="new-settings__section-subtitle">
						Manage Office Opening Hours and Weekly Schedule
					</p>
				</div>
				<div
					v-if="!isFetchingOfficeHours"
					class="btn-pill-group new-settings__section-actions"
				>
					<template v-if="isEditing">
						<button
							type="button"
							class="btn-pill btn-pill--sm"
							:disabled="busy"
							@click="cancelEditing"
						>
							Cancel
						</button>
						<button
							type="button"
							class="btn-pill btn-pill--sm"
							:disabled="busy || disableSavebtn"
							@click="onSaveClick"
						>
							Save
						</button>
					</template>
					<button
						v-else
						type="button"
						class="btn-pill btn-pill--sm"
						@click="startEditing"
					>
						Edit
					</button>
				</div>
			</div>
		</header>

		<div class="new-settings__card office-hours-section__table-card">
			<div class="office-hours-section__table-body">
				<engage-loader v-if="isFetchingOfficeHours" overlay />
				<div class="office-hours-section__table-scroll">
					<table class="office-hours-section__table">
						<colgroup>
							<col class="office-hours-section__col-day" />
							<col class="office-hours-section__col-toggle" />
							<col
								span="4"
								class="office-hours-section__col-time"
							/>
						</colgroup>
						<thead>
							<tr class="office-hours-section__table-group-row">
								<th
									scope="col"
									class="office-hours-section__table-group-spacer"
									aria-hidden="true"
								/>
								<th
									scope="col"
									class="office-hours-section__table-group-spacer"
									aria-hidden="true"
								/>
								<th colspan="2" scope="colgroup">
									OFFICE HOURS
								</th>
								<th colspan="2" scope="colgroup">
									LUNCH HOURS
								</th>
							</tr>
							<tr class="office-hours-section__table-subhead-row">
								<th
									scope="col"
									class="office-hours-section__table-subhead-day"
								>
									DAY
								</th>
								<th
									scope="col"
									class="office-hours-section__table-subhead-toggle"
								>
									OPEN/CLOSED
								</th>
								<th scope="col">START</th>
								<th scope="col">END</th>
								<th scope="col">START</th>
								<th scope="col">END</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(day, index) in officeHours"
								:key="day.day_name"
							>
								<th
									scope="row"
									class="office-hours-section__day"
								>
									{{ day.day_name }}
								</th>
								<td class="office-hours-section__toggle-cell">
									<label class="office-hours-section__switch">
										<input
											type="checkbox"
											:checked="day.is_open"
											:disabled="!isEditing"
											@change="
												updateOpenStatus(
													index,
													$event.target.checked
												)
											"
										/>
										<span
											class="office-hours-section__slider"
											:class="{
												close: !day.is_open,
											}"
										>
											{{
												day.is_open ? 'Open' : 'Closed'
											}}
										</span>
									</label>
								</td>
								<td class="office-hours-section__time-cell">
									<span
										v-if="
											getTimeCellMode(
												day,
												'starts_at'
											) === 'dash'
										"
										class="office-hours-section__time-placeholder office-hours-section__time-placeholder--dash"
										aria-hidden="true"
										>–</span
									>
									<span
										v-else-if="
											getTimeCellMode(
												day,
												'starts_at'
											) === 'all-day'
										"
										class="office-hours-section__time-placeholder"
									>
										All day
									</span>
									<div
										v-else
										class="office-hours-section__time-field"
										:class="{
											'office-hours-section__time-field--disabled': !day.is_open,
										}"
									>
										<input
											:value="day.starts_at"
											type="time"
											class="office-hours-section__time-input"
											:disabled="
												!isEditing || !day.is_open
											"
											@input="
												updateField(
													day.day_name,
													'starts_at',
													$event.target.value
												)
											"
										/>
										<font-awesome-icon
											class="office-hours-section__time-icon"
											:icon="['far', 'clock']"
											aria-hidden="true"
										/>
									</div>
								</td>
								<td class="office-hours-section__time-cell">
									<span
										v-if="
											getTimeCellMode(day, 'ends_at') ===
												'dash'
										"
										class="office-hours-section__time-placeholder office-hours-section__time-placeholder--dash"
										aria-hidden="true"
										>–</span
									>
									<span
										v-else-if="
											getTimeCellMode(day, 'ends_at') ===
												'all-day'
										"
										class="office-hours-section__time-placeholder"
									>
										All day
									</span>
									<div
										v-else
										class="office-hours-section__time-field"
										:class="{
											'office-hours-section__time-field--disabled': !day.is_open,
										}"
									>
										<input
											:value="day.ends_at"
											type="time"
											class="office-hours-section__time-input"
											:disabled="
												!isEditing || !day.is_open
											"
											@input="
												updateField(
													day.day_name,
													'ends_at',
													$event.target.value
												)
											"
											@blur="
												onTimeBlur(
													day.day_name,
													'ends_at',
													day.ends_at
												)
											"
										/>
										<font-awesome-icon
											class="office-hours-section__time-icon"
											:icon="['far', 'clock']"
											aria-hidden="true"
										/>
									</div>
								</td>
								<td class="office-hours-section__time-cell">
									<span
										v-if="
											getTimeCellMode(
												day,
												'lunch_starts_at'
											) === 'dash'
										"
										class="office-hours-section__time-placeholder office-hours-section__time-placeholder--dash"
										aria-hidden="true"
										>–</span
									>
									<span
										v-else-if="
											getTimeCellMode(
												day,
												'lunch_starts_at'
											) === 'all-day'
										"
										class="office-hours-section__time-placeholder"
									>
										All day
									</span>
									<div
										v-else
										class="office-hours-section__time-field"
										:class="{
											'office-hours-section__time-field--disabled': !day.is_open,
										}"
									>
										<input
											:value="day.lunch_starts_at"
											type="time"
											class="office-hours-section__time-input"
											:disabled="
												!isEditing || !day.is_open
											"
											@input="
												updateField(
													day.day_name,
													'lunch_starts_at',
													$event.target.value
												)
											"
										/>
										<font-awesome-icon
											class="office-hours-section__time-icon"
											:icon="['far', 'clock']"
											aria-hidden="true"
										/>
									</div>
								</td>
								<td class="office-hours-section__time-cell">
									<span
										v-if="
											getTimeCellMode(
												day,
												'lunch_ends_at'
											) === 'dash'
										"
										class="office-hours-section__time-placeholder office-hours-section__time-placeholder--dash"
										aria-hidden="true"
										>–</span
									>
									<span
										v-else-if="
											getTimeCellMode(
												day,
												'lunch_ends_at'
											) === 'all-day'
										"
										class="office-hours-section__time-placeholder"
									>
										All day
									</span>
									<div
										v-else
										class="office-hours-section__time-field"
										:class="{
											'office-hours-section__time-field--disabled': !day.is_open,
										}"
									>
										<input
											:value="day.lunch_ends_at"
											type="time"
											class="office-hours-section__time-input"
											:disabled="
												!isEditing || !day.is_open
											"
											@input="
												updateField(
													day.day_name,
													'lunch_ends_at',
													$event.target.value
												)
											"
											@blur="
												onTimeBlur(
													day.day_name,
													'lunch_ends_at',
													day.lunch_ends_at
												)
											"
										/>
										<font-awesome-icon
											class="office-hours-section__time-icon"
											:icon="['far', 'clock']"
											aria-hidden="true"
										/>
									</div>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div
					v-if="isEditing && disableSavebtn && !isFetchingOfficeHours"
					class="office-hours-section__error"
				>
					{{ errorMessage }}
				</div>
			</div>
		</div>
	</section>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import {
	DEFAULT_OFFICE_TIMINGS,
	OFFICE_DAYS,
	EMPTY_SCHEDULE_ERROR_MESSAGE,
	IVR_OFFICE_HOURS_ERROR_MESSAGE,
} from '@/config/constants'
import messages from '@/config/messages'
import util from '@/util'
import EngageLoader from '@/components/EngageLoader.vue'

const isScheduleTimeEmpty = v => v == null || v === ''

/**
 * Office hours settings section with inline schedule editing.
 */
export default {
	components: {
		EngageLoader,
	},

	computed: {
		/**
		 * Whether the active partner has an IVR configured.
		 *
		 * @return {Boolean}
		 */
		isIVRExist() {
			return this.partner?.ivr_exists
		},

		/**
		 * Whether the active partner has lunch IVR configured.
		 *
		 * @return {Boolean}
		 */
		isLunchIVRExist() {
			return this.partner?.lunch_ivr_exists
		},

		/**
		 * True when every day is closed and all time fields are empty.
		 *
		 * @return {Boolean}
		 */
		scheduleIsEmpty() {
			const isEveryClosed = this.officeHours.every(day => !day.is_open)
			const isEveryTimeEmpty = this.officeHours.every(
				day =>
					isScheduleTimeEmpty(day.starts_at) &&
					isScheduleTimeEmpty(day.ends_at) &&
					isScheduleTimeEmpty(day.lunch_starts_at) &&
					isScheduleTimeEmpty(day.lunch_ends_at)
			)
			return isEveryClosed && isEveryTimeEmpty
		},

		/**
		 * True when every lunch time field is empty.
		 *
		 * @return {Boolean}
		 */
		isEveryLunchTimeEmpty() {
			return this.officeHours.every(
				day =>
					isScheduleTimeEmpty(day.lunch_starts_at) &&
					isScheduleTimeEmpty(day.lunch_ends_at)
			)
		},

		/**
		 * True when lunch IVR exists but lunch times are empty.
		 *
		 * @return {Boolean}
		 */
		emptyLunchWhileLunchIvr() {
			return this.isLunchIVRExist && this.isEveryLunchTimeEmpty
		},

		/**
		 * Disable save when IVR rules are not satisfied.
		 *
		 * @return {Boolean}
		 */
		disableSavebtn() {
			const ivrNeedsSchedule = this.isIVRExist && this.scheduleIsEmpty
			return ivrNeedsSchedule || this.emptyLunchWhileLunchIvr
		},

		/**
		 * Validation message shown when save is disabled.
		 *
		 * @return {String}
		 */
		errorMessage() {
			return this.emptyLunchWhileLunchIvr
				? IVR_OFFICE_HOURS_ERROR_MESSAGE
				: EMPTY_SCHEDULE_ERROR_MESSAGE
		},

		...mapGetters({ partner: 'partners/active' }),
	},

	/**
	 * The component's local methods.
	 *
	 * @type {Object}
	 */
	methods: {
		/**
		 * Load office hours from the API.
		 *
		 * @return {Promise<void>}
		 */
		async fetchOfficeHours() {
			try {
				this.isFetchingOfficeHours = true
				const response = await this.$api
					.partners(this.partner.id)
					.schedules()
					.get()

				const apiData = response.get('data', [])
				this.updateOfficeHoursFromAPI(apiData)
			} catch (e) {
				this.$alert.error(e)
			} finally {
				this.isFetchingOfficeHours = false
			}
		},

		/**
		 * Update open/closed state for a day and apply defaults when opening.
		 *
		 * @param {Number} index
		 * @param {Boolean} isChecked
		 * @return {void}
		 */
		updateOpenStatus(index, isChecked) {
			this.$set(this.officeHours[index], 'is_open', isChecked)

			if (isChecked) {
				const defaults = DEFAULT_OFFICE_TIMINGS
				const timing = this.officeHours[index]
				const keys = Object.keys(defaults)
				const hasAllEmpty = keys.every(key => !timing[key])

				if (hasAllEmpty) {
					for (const [key, defaultValue] of Object.entries(
						defaults
					)) {
						if (!this.officeHours[index][key]) {
							this.$set(
								this.officeHours[index],
								key,
								defaultValue
							)
						}
					}
				}
			}
		},

		/**
		 * Update a single schedule field for the given day.
		 *
		 * @param {String} dayName
		 * @param {String} field
		 * @param {String} value
		 * @return {void}
		 */
		updateField(dayName, field, value) {
			const dayIndex = this.officeHours.findIndex(
				d => d.day_name === dayName
			)
			if (dayIndex !== -1) {
				this.$set(this.officeHours[dayIndex], field, value)
			}
		},

		/**
		 * Normalize end-of-day midnight input to 23:59.
		 *
		 * @param {String} dayName
		 * @param {String} field
		 * @param {String} value
		 * @return {void}
		 */
		onTimeBlur(dayName, field, value) {
			if (value === '00:00') {
				this.updateField(dayName, field, '23:59')
			}
		},

		/**
		 * Merge API schedule data into local office hours state.
		 *
		 * @param {Array} apiData
		 * @return {void}
		 */
		updateOfficeHoursFromAPI(apiData) {
			this.officeHours = this.officeHours.map(localDay => {
				const apiDay = apiData.find(
					d =>
						d.day_name.toLowerCase() ===
						localDay.day_name.toLowerCase()
				)
				if (!apiDay) {
					return localDay
				}

				return {
					...this.normalizeOfficeDay({
						...localDay,
						...apiDay,
						day_name: localDay.day_name.toLowerCase(),
					}),
					day_name: localDay.day_name,
				}
			})

			this.extracted = this.extractedProperties(this.officeHours)
		},

		/**
		 * Pick comparable fields from schedule rows for change detection.
		 *
		 * @param {Array} arr
		 * @return {Array}
		 */
		extractedProperties(arr) {
			return arr.map(
				({
					starts_at,
					ends_at,
					lunch_starts_at,
					lunch_ends_at,
					day_name,
					is_open,
				}) => ({
					starts_at: starts_at ?? null,
					ends_at: ends_at ?? null,
					lunch_starts_at: lunch_starts_at ?? null,
					lunch_ends_at: lunch_ends_at ?? null,
					day_name: day_name ?? null,
					is_open: is_open ?? false,
				})
			)
		},

		/**
		 * Compare two schedule arrays for equality.
		 *
		 * @param {Array} arr1
		 * @param {Array} arr2
		 * @return {Boolean}
		 */
		isOfficeHoursNotChanged(arr1, arr2) {
			const x = this.extractedProperties(arr1)
			const i = this.extractedProperties(arr2)
			const y = x.map(day => ({
				...day,
				day_name: day.day_name.toLowerCase(),
			}))
			const z = i.map(day => ({
				...day,
				day_name: day.day_name.toLowerCase(),
			}))
			const a = _.sortBy(y, ['day_name'])
			const b = _.sortBy(z, ['day_name'])
			return _.isEqual(a, b)
		},

		/**
		 * Normalize a single day for API payload.
		 *
		 * @param {Object} day
		 * @return {Object}
		 */
		normalizeOfficeDay(day) {
			return {
				...day,
				day_name: day.day_name.toLowerCase(),
				is_open: day.is_open ?? false,
				starts_at: day.starts_at ? day.starts_at : null,
				lunch_starts_at: day.lunch_starts_at
					? day.lunch_starts_at
					: null,
				lunch_ends_at: day.lunch_ends_at ? day.lunch_ends_at : null,
				ends_at: day.ends_at ? day.ends_at : null,
			}
		},

		/**
		 * Build the API payload from current office hours.
		 *
		 * @return {Array}
		 */
		getModifiedPayload() {
			return this.officeHours.map(day => this.normalizeOfficeDay(day))
		},

		/**
		 * Lowercase day names for API submission.
		 *
		 * @return {Array}
		 */
		daynameToLowerCase() {
			return this.officeHours?.map(d => ({
				...d,
				day_name: d.day_name.toLowerCase(),
			}))
		},

		/**
		 * Whether time inputs should render for a day row.
		 *
		 * @param {Object} day
		 * @param {String} field
		 * @return {Boolean}
		 */
		shouldShowTimeField(day, field) {
			if (day.is_open) {
				return true
			}

			return !this.isTimeValueEmpty(day[field])
		},

		/**
		 * Whether a schedule time value is empty (null or blank).
		 *
		 * @param {String|null} value
		 * @return {Boolean}
		 */
		isTimeValueEmpty(value) {
			return isScheduleTimeEmpty(value)
		},

		/**
		 * Cell display mode for a time field: dash, all-day label, or time input.
		 *
		 * @param {Object} day
		 * @param {String} field
		 * @return {'dash'|'all-day'|'input'}
		 */
		getTimeCellMode(day, field) {
			if (!this.shouldShowTimeField(day, field)) {
				return 'dash'
			}

			if (
				day.is_open &&
				this.isTimeValueEmpty(day[field]) &&
				this.isEditing === false
			) {
				return 'placeholder'
			}

			return 'input'
		},

		/**
		 * Persist office hours to the API.
		 *
		 * @return {Promise<void>}
		 */
		async officeHoursSubmit() {
			try {
				this.busy = true
				const payload = this.daynameToLowerCase()

				for (const day of payload) {
					if (day?.is_open) {
						if (!day.starts_at && !day.ends_at) {
							this.$alert.error(
								'Office start and end times are required.'
							)
							return
						}

						if (
							Boolean(day.starts_at) !== Boolean(day.ends_at) ||
							day.starts_at > day.ends_at
						) {
							this.$alert.error('Please enter a valid time.')
							return
						}

						if (
							Boolean(day.lunch_starts_at) !==
								Boolean(day.lunch_ends_at) ||
							day.lunch_starts_at > day.lunch_ends_at
						) {
							this.$alert.error('Please enter a valid time.')
							return
						}

						if (day.lunch_starts_at && day.lunch_ends_at) {
							if (
								day.lunch_starts_at < day.starts_at ||
								day.lunch_ends_at > day.ends_at
							) {
								this.$alert.error(
									'Lunch time must be within office hours.'
								)
								return
							}
						}

						if (day.starts_at && day.ends_at) {
							if (day.starts_at === day.ends_at) {
								this.$alert.error(
									'Office start and end times cannot be the same.'
								)
								return
							}
						}

						if (day.lunch_starts_at && day.lunch_ends_at) {
							if (day.lunch_starts_at === day.lunch_ends_at) {
								this.$alert.error(
									'Lunch start and end times cannot be the same.'
								)
								return
							}
						}
					}
				}

				const perfectPayload = this.getModifiedPayload()

				const response = await this.$api
					.partners(this.partner.id)
					.schedules()
					.create(perfectPayload)

				const data = response.get('data', [])

				await this.getOfficestatus(data)

				this.updateOfficeHoursFromAPI(data)

				this.$alert.success(
					response?.response?.message ||
						'Office hours saved successfully.'
				)

				return true
			} catch (e) {
				this.$alert.error(
					e?.response?.errors?.schedules?.[0] ||
						e?.response?.message ||
						e?.message
				)
				return false
			} finally {
				this.busy = false
			}
		},

		/**
		 * Reset office hours to the default week template.
		 *
		 * @return {void}
		 */
		clearOfficeHours() {
			this.officeHours = OFFICE_DAYS.map(i => ({
				day_name: i,
				starts_at: null,
				ends_at: null,
				is_open: false,
				lunch_starts_at: null,
				lunch_ends_at: null,
			}))
		},

		/**
		 * Reset local edit state and schedule rows before loading another partner.
		 *
		 * @return {void}
		 */
		resetOfficeHoursSection() {
			this.isEditing = false
			this.busy = false
			this.clearOfficeHours()
			this.extracted = []
		},

		/**
		 * Fetch office hours and partner IVR flags.
		 *
		 * @return {Promise<void>}
		 */
		async loadOfficeHours() {
			if (!this.partner?.id) {
				return
			}

			try {
				this.isFetchingOfficeHours = true
				await this.fetchOfficeHours()
				await this.fetchPartner(this.partner.id)
			} catch (e) {
				this.$alert.error(messages.defaultErrorMessage)
			} finally {
				this.isFetchingOfficeHours = false
			}
		},

		/**
		 * Enter edit mode.
		 *
		 * @return {void}
		 */
		startEditing() {
			this.isEditing = true
		},

		/**
		 * Discard unsaved changes and exit edit mode.
		 *
		 * @return {void}
		 */
		cancelEditing() {
			this.restoreFromExtracted()
			this.isEditing = false
		},

		/**
		 * Restore office hours from the last saved snapshot.
		 *
		 * @return {void}
		 */
		restoreFromExtracted() {
			this.officeHours = this.extracted.map(item => ({
				day_name: util.capitalizeFirstLetter(item.day_name),
				starts_at: item.starts_at,
				ends_at: item.ends_at,
				lunch_starts_at: item.lunch_starts_at,
				lunch_ends_at: item.lunch_ends_at,
				is_open: item.is_open,
			}))
		},

		/**
		 * Save office hours and exit edit mode on success.
		 *
		 * @return {Promise<void>}
		 */
		async onSaveClick() {
			if (this.disableSavebtn) {
				return
			}

			const saved = await this.officeHoursSubmit()
			if (saved) {
				this.isEditing = false
			}
		},

		cancelAllEditing() {
			if (this.isEditing) {
				this.cancelEditing()
			}
		},

		...mapActions({
			getOfficestatus: 'settings/getOfficestatus',
			fetchPartner: 'partners/find',
		}),
	},

	name: 'OfficeHoursSection',

	/**
	 * The component's property watchers.
	 *
	 * @type {Object}
	 */
	watch: {
		/**
		 * Reload schedule when the active partner changes (e.g. route partner switch).
		 *
		 * @param {Number} newId
		 * @param {Number|undefined} oldId
		 * @return {Promise<void>}
		 */
		async 'partner.id'(newId, oldId) {
			if (!newId || newId === oldId) {
				return
			}

			if (oldId != null) {
				this.resetOfficeHoursSection()
			}

			await this.loadOfficeHours()
		},
	},

	/**
	 * Load schedule data when the section is shown.
	 *
	 * @return {Promise<void>}
	 */
	async mounted() {
		await this.loadOfficeHours()
	},

	beforeDestroy() {
		this.cancelAllEditing()
	},

	deactivated() {
		this.cancelAllEditing()
	},

	/**
	 * Get the component's initial state.
	 *
	 * @return {Object}
	 */
	data() {
		return {
			isEditing: false,
			officeHours: OFFICE_DAYS.map(i => ({
				day_name: i,
				starts_at: null,
				ends_at: null,
				is_open: false,
				lunch_starts_at: null,
				lunch_ends_at: null,
			})),
			busy: false,
			extracted: [],
			isFetchingOfficeHours: false,
		}
	},
}
</script>
